/**
 * Static invite-preview payloads for the `/invites/accept` mockup-mode
 * page (task 104d.1). Four variants keyed by token suffix so reviewers
 * can eyeball every branch without a backend:
 *
 *   token suffix includes …  →  variant
 *   -----------------------     ---------
 *   "expired"                →  expired (expiresAt in the past)
 *   "revoked"                →  revoked
 *   "accepted"               →  already accepted
 *   everything else          →  pending (the happy path)
 *
 * 104d.2 replaces this with a real call to
 *   GET /api/v1/invites/:token/preview
 * so the single-fixture convention (no Zod schema) is fine here.
 *
 * All timestamps derive from FROZEN_NOW_MS — no new Date() elsewhere.
 */

import { FROZEN_NOW_MS } from '@/lib/frozenTime';

const DAY_MS = 24 * 60 * 60 * 1000;

export type InvitePreviewStatus = 'pending' | 'expired' | 'revoked' | 'accepted';

export interface InvitePreview {
  orgName: string;
  role: 'owner' | 'admin' | 'member';
  inviterName: string;
  email: string;
  expiresAt: string;
  status: InvitePreviewStatus;
}

const BASE: InvitePreview = {
  orgName: 'Meridian & Associates',
  role: 'member',
  inviterName: 'Sarah Chen',
  email: 'jordan.reyes@firm.com',
  expiresAt: new Date(FROZEN_NOW_MS + 5 * DAY_MS).toISOString(),
  status: 'pending',
};

const EXPIRED: InvitePreview = {
  ...BASE,
  status: 'expired',
  expiresAt: new Date(FROZEN_NOW_MS - 2 * DAY_MS).toISOString(),
};

const REVOKED: InvitePreview = { ...BASE, status: 'revoked' };
const ACCEPTED: InvitePreview = { ...BASE, status: 'accepted' };

export function mockInvitePreview(token: string | null): InvitePreview {
  const suffix = (token ?? '').toLowerCase();
  if (suffix.includes('expired')) return EXPIRED;
  if (suffix.includes('revoked')) return REVOKED;
  if (suffix.includes('accepted')) return ACCEPTED;
  return BASE;
}
